// routes/returnTo.ts — where to send someone AFTER they sign in.
//
// F-137: opening a deep link (or reloading) while signed out sent the user to /signin and
// then dumped them on /home, losing the screen they actually asked for. Router `location
// state` cannot carry that intent here, because the sign-in flow navigates away to /verify
// and back, and on the web a reload throws history state away entirely. So the requested
// path is parked here: in memory for the common case, mirrored into sessionStorage so it
// survives a full page load inside the same tab.
//
// ⚠️ NOT localStorage. A destination that outlives the tab is a stale destination — sign in
// tomorrow and you would be thrown at last week's screen.

const STORAGE_KEY = "arc.returnTo";

/** Where sign-in lands when nothing specific was asked for. */
export const DEFAULT_AFTER_SIGN_IN = "/home";

/**
 * Paths that must never be replayed after sign-in. Sending someone back to the sign-in
 * flow they just completed is a loop; `/oauth/callback` is a one-shot code exchange and
 * `/ifu` leaves the app for a PDF.
 */
const NEVER_RESTORE = ["/signin", "/signup", "/verify", "/oauth/callback", "/ifu"];

export interface PartialLocation {
  pathname: string;
  search?: string;
  hash?: string;
}

/**
 * A path is restorable only if it is app-internal. sessionStorage is writable by anything
 * running in the page, so this is re-checked on the way OUT as well as on the way in —
 * without it, a planted value would turn the sign-in redirect into an open redirect.
 */
export const isRestorablePath = (path: unknown): path is string => {
  if (typeof path !== "string" || path.length === 0) return false;
  if (!path.startsWith("/")) return false; // absolute URLs, scheme-relative, bare words
  if (path.startsWith("//")) return false; // //evil.example.com
  if (path.includes("\\")) return false; // backslash tricks some URL parsers into a host
  const pathname = path.split(/[?#]/)[0];
  return !NEVER_RESTORE.includes(pathname);
};

/** Flatten a router location into the path string `history.push` wants. */
export const toPath = (location: PartialLocation): string =>
  `${location.pathname}${location.search || ""}${location.hash || ""}`;

let pending: string | null = null;

const readStored = (): string | null => {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    // Private mode, a locked-down webview, or no window at all (tests). In-memory only.
    return null;
  }
};

const writeStored = (value: string | null): void => {
  try {
    if (value === null) window.sessionStorage.removeItem(STORAGE_KEY);
    else window.sessionStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Non-fatal: `pending` still carries the destination for this page load.
  }
};

/**
 * Record the screen the user asked for, to be replayed once they are signed in.
 * Called at the moment the router turns an unauthenticated request away.
 */
export const rememberReturnTo = (location: PartialLocation): void => {
  const path = toPath(location);
  if (!isRestorablePath(path)) return;
  pending = path;
  writeStored(path);
};

const currentPending = (): string | null => {
  const value = pending ?? readStored();
  return isRestorablePath(value) ? value : null;
};

/** Read the destination without spending it. Safe to call from render. */
export const peekReturnTo = (): string => currentPending() ?? DEFAULT_AFTER_SIGN_IN;

/**
 * Read the destination and spend it, so a later sign-in does not replay it.
 * Sign-in completion calls this; the router only ever peeks.
 */
export const takeReturnTo = (): string => {
  const value = currentPending() ?? DEFAULT_AFTER_SIGN_IN;
  clearReturnTo();
  return value;
};

export const clearReturnTo = (): void => {
  pending = null;
  writeStored(null);
};
